/*
 * Rooté Bloom ingredient library.
 *
 * Drives sections/rb-ingredient-library.liquid: category chips, a substring search and
 * a detail dialog. Every card is rendered server-side; this only ever toggles
 * visibility, so the page is complete and indexable with JavaScript off.
 *
 * The dialog is populated by cloning the card's own hidden detail block rather than
 * from a data structure, which keeps the copy in one place — the Liquid.
 */

if (!customElements.get('rb-ingredient-library')) {
  const normalize = (value) => value.toLowerCase().trim();

  class RbIngredientLibrary extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.chips = Array.from(this.querySelectorAll('[data-filter]'));
      this.searchInput = this.querySelector('[data-search]');
      this.countNode = this.querySelector('[data-count]');
      this.emptyNode = this.querySelector('[data-empty]');
      this.dialog = this.querySelector('[data-dialog]');
      this.dialogBody = this.querySelector('[data-dialog-body]');

      this.category = 'all';
      this.query = '';
      this.lastOpener = null;

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => this.selectCategory(chip.dataset.filter));
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => {
          this.query = normalize(this.searchInput.value);
          this.apply();
        });
      }

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open]');
        if (opener) this.open(opener.closest('[data-card]'), opener);
      });

      if (this.dialog) {
        this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('close', () => this.lastOpener?.focus());
        /* A native dialog closes on a backdrop click only if we ask it to. */
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
      }

      this.apply();
    }

    selectCategory(category) {
      this.category = category;
      this.chips.forEach((chip) => {
        chip.setAttribute('aria-pressed', String(chip.dataset.filter === category));
      });
      this.apply();
    }

    matches(card) {
      const inCategory = this.category === 'all' || card.dataset.category === this.category;
      if (!inCategory) return false;
      if (!this.query) return true;
      return (card.dataset.searchText || '').includes(this.query);
    }

    apply() {
      let visible = 0;

      this.cards.forEach((card) => {
        const shown = this.matches(card);
        card.hidden = !shown;
        if (shown) visible += 1;
      });

      if (this.countNode) this.countNode.textContent = this.countLabel(visible);
      if (this.emptyNode) this.emptyNode.hidden = visible > 0;
    }

    /*
     * "30 ingredients", in whatever the page language is. The forms come from the section's
     * `data-count-forms` payload, which Liquid renders through the `t` filter; Intl.PluralRules
     * picks the right one, which matters because Arabic has six categories to English's two.
     */
    countLabel(visible) {
      if (!this.countForms) {
        const payload = this.querySelector('[data-count-forms]');
        try {
          this.countForms = payload ? JSON.parse(payload.textContent) : {};
        } catch (error) {
          this.countForms = {};
        }
        this.pluralRules = new Intl.PluralRules(document.documentElement.lang || 'en');
      }
      const category = this.pluralRules.select(visible);
      const form = this.countForms[category] || this.countForms.other || '';
      return form.replace('{{ count }}', visible);
    }

    open(card, opener) {
      if (!card || !this.dialog || !this.dialogBody) return;

      const detail = card.querySelector('[data-detail]');
      if (!detail) return;

      const contents = detail.cloneNode(true);
      contents.removeAttribute('hidden');
      contents.removeAttribute('data-detail');

      this.dialogBody.replaceChildren(contents);
      this.lastOpener = opener;

      const heading = this.dialogBody.querySelector('[data-detail-name]');
      if (heading) heading.id = this.dialog.getAttribute('aria-labelledby');

      this.dialog.showModal();
    }
  }

  customElements.define('rb-ingredient-library', RbIngredientLibrary);
}
